﻿// Returns a theme callback for react-select. `mode` is the app theme
// string ('dark-mode' | 'light-mode'); the returned function receives
// react-select's default theme object and layers our color overrides on top.
//
// react-select drives nearly everything (chip background, border, placeholder,
// indicators, menu option text) off its `neutral0`–`neutral90` ramp. We mirror
// the app's slate palette here so the control sits on the same surface as
// .form-control / .form-select (i.e. --bs-tertiary-bg = #111a2e) instead of
// drifting back to the default Bootstrap-grey ramp. Light mode keeps defaults.
export const customSelectTheme = (mode) => (theme) => {
    const isDark = mode === 'dark-mode';
    return {
        ...theme,
        colors: {
            ...theme.colors,
            primary:   '#26ada9',
            primary25: isDark ? 'rgba(38, 173, 169, 0.25)' : '#ccefee',
            primary50: isDark ? 'rgba(38, 173, 169, 0.4)'  : '#99dfdd',
            primary75: isDark ? 'rgba(38, 173, 169, 0.6)'  : '#66cfcc',

            neutral0:  isDark ? '#111a2e' : theme.colors.neutral0,  // control + menu bg (--bs-tertiary-bg)
            neutral5:  isDark ? '#0f172a' : theme.colors.neutral5,  // disabled bg       (--bs-body-bg)
            neutral10: isDark ? '#1e293b' : theme.colors.neutral10, // multi-value chip  (--bs-secondary-bg)
            neutral20: isDark ? '#334155' : theme.colors.neutral20, // border — slate-700 flat equivalent of the translucent --bs-border-color
            neutral30: isDark ? '#475569' : theme.colors.neutral30, // hovered border — slate-600
            neutral40: isDark ? '#94a3b8' : theme.colors.neutral40, // indicator         (--bs-secondary-color)
            neutral50: isDark ? '#94a3b8' : theme.colors.neutral50, // placeholder       (--bs-secondary-color)
            neutral60: isDark ? '#cbd5e1' : theme.colors.neutral60, // hovered indicator (--bs-body-color)
            neutral70: isDark ? '#cbd5e1' : theme.colors.neutral70,
            neutral80: isDark ? '#cbd5e1' : theme.colors.neutral80, // text              (--bs-body-color)
            neutral90: isDark ? '#f1f5f9' : theme.colors.neutral90, // emphasis          (--bs-emphasis-color)
        },
    };
};

// Slightly different control/menu background — used where the surrounding
// surface is already a card/modal and the select needs to recede a bit.
export const customLighterTheme = (mode) => (theme) => {
    const base = customSelectTheme(mode)(theme);
    const isDark = mode === 'dark-mode';
    return {
        ...base,
        colors: {
            ...base.colors,
            neutral0: isDark ? '#1e293b' : '#f8f8f8',
        },
    };
};

export const selectStyle = {
    option: (styles, { isSelected }) => ({
        ...styles,
        fontFamily: "Hanken Grotesk, Sans-serif",
        fontWeight: isSelected ? "bold" : "normal",
        color: isSelected ? "#fff" : styles.color,

        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: "1.2rem",
        paddingTop: 8,
        paddingBottom: 8,
    }),

    container: (provided) => ({ ...provided, width: "100%" }),

    control: (base, state) => ({
        ...base,
        // Let react-select's themed neutral20 drive the resting border; only
        // override on focus so the focus ring stays our brand blue in both modes.
        borderColor: state.isFocused ? '#57aefe' : base.borderColor,
        boxShadow: state.isFocused ? '0 0 0 1px #57aefe' : 'none',
        '&:hover': {
            borderColor: state.isFocused ? '#57aefe' : base.borderColor,
        },
    }),

    menuList: (base) => ({
        ...base,
        padding: 0
    }),

    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
    menu: (base) => ({ ...base, zIndex: 2000 }),
};


selectStyle.container = (provided) => ({ ...provided, width: '100%' });

// Compact style for inline multi-selects (e.g. dashboard day cell team member picker)
export const cellSelectStyle = {
    ...selectStyle,
    control: (base, state) => ({
        ...base,
        minHeight: '30px',
        borderColor: state.isFocused ? '#57aefe' : base.borderColor,
        boxShadow: state.isFocused ? '0 0 0 1px #57aefe' : 'none',
        '&:hover': {
            borderColor: state.isFocused ? '#57aefe' : base.borderColor,
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '2px 6px',
        gap: '2px',
    }),
    multiValue: (base) => ({
        ...base,
        margin: '1px 2px',
        borderRadius: '4px',
    }),
    multiValueLabel: (base) => ({
        ...base,
        fontSize: '11px',
        padding: '1px 4px',
        paddingLeft: '4px',
    }),
    multiValueRemove: (base) => ({
        ...base,
        padding: '0 3px',
    }),
    input: (base) => ({
        ...base,
        fontSize: '11px',
        margin: 0,
        padding: 0,
    }),
    placeholder: (base) => ({
        ...base,
        fontSize: '11px',
    }),
    option: (styles, { isSelected }) => ({
        ...styles,
        fontSize: '11px',
        fontFamily: 'Hanken Grotesk, Sans-serif',
        fontWeight: isSelected ? 'bold' : 'normal',
        color: isSelected ? '#fff' : styles.color,
        paddingTop: 5,
        paddingBottom: 5,
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: '30px',
    }),
    dropdownIndicator: () => ({ display: 'none' }),
    indicatorSeparator: () => ({ display: 'none' }),
    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
    menu: (base) => ({ ...base, zIndex: 2000 }),
    menuList: (base) => ({ ...base, padding: 0 }),
    container: (provided) => ({ ...provided, width: '100%' }),
};